import {
	createLog,
	LogSeverity,
	OPERATIONS_SECTIONS,
	type RegistrationServiceConfig,
	type RegistrationServiceConfigDtoInput,
	type RegistrationServiceConfigDtoOutput,
	RegistrationServiceConfigDtoSchema,
	Services,
	type ServicesProvider,
	SETTINGS_GROUPS,
} from '@ekhodealer/ekho-common';
import { Note } from '@ekhodealer/ekho-common/rev';

import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';

import { useUpdateRegistrationService } from '../../../api/configuration/services/useUpdateRegistrationService';
import { useServiceProgramInfo } from '../../../hooks/useServiceProgramInfo';
import { FormSection } from '../../config-mgmt/FormSection';

/**
 * Helper function to get initial form data from ServicesProvider
 */
function getDefaultValues(
	servicesProvider: ServicesProvider | undefined | null,
): RegistrationServiceConfigDtoInput {
	const registrationConfig = servicesProvider?.config?.[Services.REGISTRATION] as
		| RegistrationServiceConfig
		| undefined;

	return {
		supported: registrationConfig?.supported ?? false,
	};
}

export function RegistrationServiceForm() {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const programInfo = useServiceProgramInfo(OPERATIONS_SECTIONS.REGISTRATION);
	const updateRegistrationServiceMutation = useUpdateRegistrationService();

	// Early return if service is not part of the program
	if (!programInfo.shouldRender) {
		return null;
	}

	// Handle form submit
	const handleSubmit = async (
		data: RegistrationServiceConfigDtoOutput,
	): Promise<{ isSuccess: boolean }> => {
		if (!servicesProvider) return { isSuccess: false };

		try {
			await updateRegistrationServiceMutation.mutateAsync({
				supported: data.supported,
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to update registration service configuration: ${
					error instanceof Error ? error.message : String(error)
				}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<FormSection
			sectionPath={{
				group: SETTINGS_GROUPS.OPERATIONS,
				section: OPERATIONS_SECTIONS.REGISTRATION,
			}}
			form={{
				schema: RegistrationServiceConfigDtoSchema,
				defaultValues: getDefaultValues(servicesProvider),
				onSubmit: handleSubmit,
			}}
			title='Registration'
			description="Enable Ekho-managed vehicle registration. When enabled, Ekho handles registering vehicles in the buyer's state on your behalf after the sale is completed."
			formTitle='Registration service'
			toggleSettings={{
				fieldName: 'supported',
				visibility: 'write',
			}}
			hideDivider>
			<Note>No configuration needed for this service</Note>
		</FormSection>
	);
}

export default RegistrationServiceForm;
